import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, Send, Trash2, Shield, AlertTriangle } from "lucide-react";
import { Sigil } from "@/lib/cosmetics";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader } from "@/components/Page";
import { btn } from "@/components/Page";
import MessageBoard from "@/components/MessageBoard";
import Modal from "@/components/Modal";
import { linkifyUrls } from "@/lib/textUtils";

const ChatIcon = ({ size = 24, className = "" }) => (
  <img
    src="/chat-icon.png"
    alt="Arena Chat"
    className={`inline-block ${className}`}
    style={{ width: size * 1.2, height: size * 1.2, objectFit: "contain" }}
  />
);

function formatExactDateTime(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

const refRoute = (r) =>
  r.kind === "gladiator" ? `/gladiator/${r.id}` : r.kind === "lanista" ? `/profile/${r.id}` : "/events?tab=boss";

function renderHerald(text, refs, navigate) {
  if (!refs || !refs.length) return text;
  const valid = refs.filter((r) => r.label && (r.id || r.kind === "beast"));
  if (!valid.length) return text;
  const sorted = [...valid].sort((a, b) => b.label.length - a.label.length);
  let nodes = [text];
  let key = 0;
  sorted.forEach((ref) => {
    const next = [];
    nodes.forEach((node) => {
      if (typeof node !== "string") {
        next.push(node);
        return;
      }
      const parts = node.split(ref.label);
      parts.forEach((p, i) => {
        if (p) next.push(p);
        if (i < parts.length - 1) {
          next.push(
            <button
              key={`r-${key++}`}
              data-testid={`herald-link-${ref.kind}`}
              onClick={() => navigate(refRoute(ref))}
              className="text-amber-200 font-semibold underline decoration-amber-700/60 underline-offset-2 hover:text-amber-100 hover:decoration-amber-400 transition-colors"
            >
              {ref.label}
            </button>
          );
        }
      });
    });
    nodes = next;
  });
  return nodes;
}

const renderTextWithMentions = (text, managers, navigate, messageId = "") => {
  if (!text) return text;
  let parts = linkifyUrls(text, messageId);
  if (typeof parts === "string") parts = [parts];
  if (!managers || managers.length === 0) return parts;
  const sortedManagers = [...managers].sort((a, b) => b.name.length - a.name.length);
  let key = 0;
  sortedManagers.forEach((manager) => {
    const next = [];
    parts.forEach((part) => {
      if (typeof part !== "string") {
        next.push(part);
        return;
      }
      const pattern = `@${manager.name}`;
      const lowerPart = part.toLowerCase();
      const lowerPattern = pattern.toLowerCase();
      let searchIdx = 0;
      let lastSplitIdx = 0;
      while (true) {
        const foundIdx = lowerPart.indexOf(lowerPattern, searchIdx);
        if (foundIdx === -1) break;
        if (foundIdx > lastSplitIdx) next.push(part.substring(lastSplitIdx, foundIdx));
        const mentionText = part.substring(foundIdx, foundIdx + pattern.length);
        next.push(
          <button
            key={`mention-${messageId}-${manager.user_id}-${key++}`}
            onClick={() => navigate(`/profile/${manager.user_id}`)}
            className="text-amber-400 hover:text-amber-300 underline font-medium"
          >
            {mentionText}
          </button>
        );
        lastSplitIdx = foundIdx + pattern.length;
        searchIdx = lastSplitIdx;
      }
      if (lastSplitIdx < part.length) next.push(part.substring(lastSplitIdx));
      else if (lastSplitIdx === 0) next.push(part);
    });
    parts = next;
  });
  return parts.length > 0 ? parts : text;
};

const renderPlainChatText = (text, messageId = "") => linkifyUrls(text, messageId);

const CHANNELS = {
  arena: {
    key: "arena",
    label: "Arena Chat",
    loadPath: "/chat",
    sendPath: "/chat",
    deletePath: (id) => `/chat/${id}`,
    subtitle:
      "Live chat with other lanistae. Be respectful to other players. No hate speech. No politics. No personal info. Failing to follow this will result in a ban from chat.",
    emptyText: "No messages yet. Be the first to speak in the arena chat.",
    placeholder: "Address the arena… (Type @ to mention someone)",
    helperText: null,
    allowMentions: true,
    dataTestId: "chat-messages",
    inputTestId: "chat-input",
    sendTestId: "chat-send-btn",
  },
  unrestricted: {
    key: "unrestricted",
    label: "18+ Chat",
    loadPath: "/chat/unrestricted",
    sendPath: "/chat/unrestricted",
    deletePath: (id) => `/chat/unrestricted/${id}`,
    subtitle:
      "Less filtered conversation for players who choose it. Moderation still applies: no racism, no porn, no advertising.",
    emptyText: "No messages yet. If you enter, say your piece.",
    placeholder: "Speak freely…",
    
    allowMentions: false,
    dataTestId: "unrestricted-chat-messages",
    inputTestId: "unrestricted-chat-input",
    sendTestId: "unrestricted-chat-send-btn",
  },
};

export default function Chat() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("arena");
  const [channelViews, setChannelViews] = useState({ arena: "all", unrestricted: "all" });
  const [activeBoardTab, setActiveBoardTab] = useState("main");
  const [arenaMessages, setArenaMessages] = useState(null);
  const [unrestrictedMessages, setUnrestrictedMessages] = useState(null);
  const [managers, setManagers] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [mentionSearch, setMentionSearch] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [unrestrictedWarningOpen, setUnrestrictedWarningOpen] = useState(false);
  const [unrestrictedAccepted, setUnrestrictedAccepted] = useState(false);
  const listRef = useRef(null);
  const atBottomRef = useRef(true);
  const inputRef = useRef(null);

  const channel = CHANNELS[activeTab];
  const activeView = channelViews[activeTab] || "all";
  const messages = activeTab === "arena" ? arenaMessages : unrestrictedMessages;
  const setMessages = activeTab === "arena" ? setArenaMessages : setUnrestrictedMessages;
  const visibleMessages = useMemo(() => {
    if (activeView === "chronicle") return (messages || []).filter((message) => message.system);
    if (activeView === "conversation") return (messages || []).filter((message) => !message.system);
    return messages || [];
  }, [activeView, messages]);

  const loadArena = async () => {
    try {
      const { data } = await api.get(CHANNELS.arena.loadPath);
      setArenaMessages(data.messages || []);
    } catch {
      setArenaMessages([]);
    }
  };

  const loadUnrestricted = async () => {
    try {
      const { data } = await api.get(CHANNELS.unrestricted.loadPath);
      setUnrestrictedMessages(data.messages || []);
      setUnrestrictedAccepted(!!data.warning_accepted);
      return data;
    } catch {
      setUnrestrictedMessages([]);
      return { warning_accepted: unrestrictedAccepted };
    }
  };

  useEffect(() => {
    loadArena();
    const t = setInterval(() => {
      if (activeTab === "arena") loadArena();
      if (activeTab === "unrestricted" && unrestrictedAccepted) loadUnrestricted();
    }, 3000);
    api.get("/managers").then((r) => setManagers(r.data.managers)).catch(() => {});
    return () => clearInterval(t);
  }, [activeTab, unrestrictedAccepted]);

  useEffect(() => {
    if (atBottomRef.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [arenaMessages, unrestrictedMessages, activeTab, activeView]);

  const openUnrestricted = async () => {
    const data = unrestrictedMessages === null ? await loadUnrestricted() : { warning_accepted: unrestrictedAccepted };
    if (!(data?.warning_accepted || unrestrictedAccepted)) {
      setUnrestrictedWarningOpen(true);
      return;
    }
    setActiveTab("unrestricted");
  };

  const acceptUnrestrictedWarning = async () => {
    try {
      await api.post("/chat/unrestricted/acknowledge");
      setUnrestrictedAccepted(true);
      setUnrestrictedWarningOpen(false);
      await refreshUser?.();
      await loadUnrestricted();
      setActiveTab("unrestricted");
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    if (!channel.allowMentions) {
      setMentionSearch(null);
      setMentionSuggestions([]);
      return;
    }
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = newText.slice(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      if (!textAfterAt.includes(" ")) {
        const filtered = managers.filter((m) => m.name.toLowerCase().includes(textAfterAt.toLowerCase())).slice(0, 5);
        setMentionSearch({ query: textAfterAt, startPos: lastAtIndex });
        setMentionSuggestions(filtered);
        setSelectedSuggestionIndex(0);
        return;
      }
    }
    setMentionSearch(null);
    setMentionSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!channel.allowMentions || mentionSuggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev < mentionSuggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : mentionSuggestions.length - 1));
    } else if (e.key === "Tab" || e.key === "Enter") {
      if (mentionSearch && mentionSuggestions.length > 0) {
        e.preventDefault();
        selectMention(mentionSuggestions[selectedSuggestionIndex]);
      }
    } else if (e.key === "Escape") {
      setMentionSearch(null);
      setMentionSuggestions([]);
    }
  };

  const selectMention = (manager) => {
    if (!mentionSearch) return;
    const before = text.slice(0, mentionSearch.startPos);
    const after = text.slice(mentionSearch.startPos + mentionSearch.query.length + 1);
    const newText = `${before}@${manager.name}${after}`;
    setText(newText);
    setMentionSearch(null);
    setMentionSuggestions([]);
    if (inputRef.current) {
      inputRef.current.focus();
      const newCursorPos = before.length + manager.name.length + 1;
      setTimeout(() => inputRef.current.setSelectionRange(newCursorPos, newCursorPos), 0);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post(channel.sendPath, { text: t });
      setText("");
      setMessages((prev) => [...(prev || []), data]);
      atBottomRef.current = true;
      if (!channel.allowMentions) {
        setMentionSearch(null);
        setMentionSuggestions([]);
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const onScroll = (e) => {
    const el = e.target;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const deleteMsg = async (id) => {
    try {
      await api.delete(channel.deletePath(id));
      setMessages((prev) => (prev || []).filter((m) => m.id !== id));
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  const renderedTabNote = useMemo(() => {
    if (!channel.helperText) return null;
    return (
      <div
        className="border border-red-900/40 bg-red-950/10 text-red-200 px-4 py-3 text-sm"
        data-testid="unrestricted-chat-channel-note"
      >
        {channel.helperText}
      </div>
    );
  }, [channel.helperText]);

  if (messages === null && activeTab === "arena") return <Loader label="Entering the arena chat…" />;
  if (activeTab === "unrestricted" && unrestrictedMessages === null && unrestrictedAccepted) return <Loader label="Entering 18+ chat…" />;

  return (
    <div className="space-y-10 pb-10">
      <div>
        <PageHeader title="Arena Chat" subtitle={channel.subtitle} icon={ChatIcon} />

        <div className="flex items-center gap-2 mb-4" data-testid="arena-chat-tabs">
          <button
            type="button"
            data-testid="arena-chat-tab"
            onClick={() => setActiveTab("arena")}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border transition-colors ${
              activeTab === "arena"
                ? "border-amber-600 bg-amber-900/20 text-amber-200"
                : "border-zinc-800 bg-zinc-950/60 text-zinc-400 hover:border-zinc-700"
            }`}
          >
            Arena Chat
          </button>
          <button
            type="button"
            data-testid="unrestricted-chat-tab"
            onClick={openUnrestricted}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border transition-colors ${
              activeTab === "unrestricted"
                ? "border-red-700 bg-red-950/20 text-red-200"
                : "border-zinc-800 bg-zinc-950/60 text-zinc-400 hover:border-zinc-700"
            }`}
          >
            18+ Chat
          </button>
        </div>

        {
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="arena-chat-view-filters">
            {[
              ["all", "All"],
              ["conversation", "Conversation"],
              ["chronicle", "Arena Chronicle"],
            ].map(([view, label]) => (
              <button
                key={view}
                type="button"
                data-testid={`${activeTab}-chat-filter-${view}`}
                onClick={() => setChannelViews((current) => ({ ...current, [activeTab]: view }))}
                className={`px-3 py-1.5 text-xs font-cinzel uppercase tracking-wide border transition-colors ${
                  activeView === view
                    ? "border-amber-600 bg-amber-900/20 text-amber-200"
                    : "border-zinc-800 bg-zinc-950/60 text-zinc-400 hover:border-zinc-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        }

        {renderedTabNote}

        <div className="flex flex-col h-[55vh] mt-4">
          <div
            ref={listRef}
            onScroll={onScroll}
            className="flex-1 overflow-y-auto border border-amber-900/30 bg-zinc-950/60 p-4 space-y-3"
            data-testid={channel.dataTestId}
          >
            {visibleMessages.length === 0 && <p data-testid={`${activeTab}-chat-filter-empty`} className="text-zinc-600 text-sm text-center py-10">{activeView === "chronicle" ? "No Arena Chronicle notices yet." : activeView === "conversation" ? "No player conversation yet." : channel.emptyText}</p>}
            {visibleMessages.map((m) => {
              if (m.system) {
                return (
                  <div key={m.id} data-testid={`chat-system-${m.id}`} className="flex justify-center my-1">
                    <div className="text-[12px] text-amber-300 bg-amber-950/20 border border-amber-900/40 px-3 py-1.5 rounded-sm text-center max-w-[85%]">
                      {renderHerald(m.text, m.refs, navigate)}
                      {user?.is_admin && (
                        <button data-testid={`chat-delete-${m.id}`} onClick={() => deleteMsg(m.id)} className="ml-2 text-zinc-500 hover:text-red-400 align-middle">
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              }
              const mine = m.user_id === user?.user_id;
              const isBlocked = m.is_blocked === true;
              return (
                <div key={m.id} data-testid={`${activeTab}-chat-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                    <div className="flex items-center gap-2 mb-0.5 px-1">
                      <span
                        data-testid={`${activeTab}-chat-name-${m.id}`}
                        className={`text-[11px] font-cinzel uppercase tracking-wide inline-flex items-center gap-1 ${
                          m.is_admin ? "text-amber-300 font-bold drop-shadow-[0_0_5px_rgba(251,191,36,0.45)]" : mine ? "text-amber-400" : "text-zinc-400"
                        }`}
                      >
                        {m.sigil && <Sigil id={m.sigil} size={14} />}
                        {m.is_admin && <Shield size={10} className="text-amber-400" />}
                        {m.user_id ? (
                          <button
                            data-testid={`${activeTab}-chat-name-link-${m.id}`}
                            onClick={() => navigate(`/profile/${m.user_id}`)}
                            className="hover:text-amber-200 hover:underline underline-offset-2 cursor-pointer transition-colors"
                            title="View profile"
                          >
                            {mine ? "You" : m.name}
                          </button>
                        ) : (
                          mine ? "You" : m.name
                        )}
                      </span>
                      <span className="text-[10px] text-zinc-600">{formatExactDateTime(m.created_at)}</span>
                      {user?.is_admin && (
                        <button data-testid={`${activeTab}-chat-delete-${m.id}`} onClick={() => deleteMsg(m.id)} className="text-zinc-600 hover:text-red-400" title="Delete (admin)">
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                    <div
                      className={`px-3 py-2 text-sm border ${
                        isBlocked ? "bg-zinc-950 border-zinc-800 text-zinc-600 italic" : mine ? "bg-amber-900/20 border-amber-700/40 text-amber-100" : "bg-zinc-900 border-zinc-800 text-zinc-200"
                      }`}
                    >
                      {channel.allowMentions
                        ? renderTextWithMentions(m.text, managers, navigate, m.id)
                        : renderPlainChatText(m.text, m.id)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {activeView !== "chronicle" && <form onSubmit={send} className="relative">
            {channel.allowMentions && mentionSuggestions.length > 0 && (
              <div className="absolute bottom-full left-0 mb-2 w-64 bg-zinc-900 border border-amber-700/40 rounded shadow-lg z-50">
                <div className="py-1">
                  {mentionSuggestions.map((manager, idx) => (
                    <button
                      key={manager.user_id}
                      type="button"
                      onClick={() => selectMention(manager)}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${idx === selectedSuggestionIndex ? "bg-amber-900/40 text-amber-200" : "text-zinc-300 hover:bg-zinc-800"}`}
                    >
                      {manager.sigil && <Sigil id={manager.sigil} size={16} />}
                      <span className="font-cinzel">{manager.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <input
                ref={inputRef}
                data-testid={channel.inputTestId}
                value={text}
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                maxLength={400}
                placeholder={channel.placeholder}
                className="flex-1 bg-zinc-900 border border-amber-900/40 px-4 py-3 text-zinc-100 placeholder-zinc-600 focus:border-amber-600 outline-none"
              />
              <button data-testid={channel.sendTestId} type="submit" disabled={busy || !text.trim()} className={btn() + " px-5"}>
                <Send size={16} />
              </button>
            </div>
          </form>}
        </div>
      </div>

      <section className="space-y-4" data-testid="arena-chat-board-section">
        <div className="flex flex-wrap items-center gap-2" data-testid="arena-chat-board-tabs">
          <button
            type="button"
            data-testid="arena-board-tab"
            onClick={() => setActiveBoardTab("main")}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border transition-colors ${
              activeBoardTab === "main"
                ? "border-amber-600 bg-amber-900/20 text-amber-200"
                : "border-zinc-800 bg-zinc-950/60 text-zinc-400 hover:border-zinc-700"
            }`}
          >
            Arena Board
          </button>
          <button
            type="button"
            data-testid="help-board-tab"
            onClick={() => setActiveBoardTab("help")}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border transition-colors ${
              activeBoardTab === "help"
                ? "border-amber-600 bg-amber-900/20 text-amber-200"
                : "border-zinc-800 bg-zinc-950/60 text-zinc-400 hover:border-zinc-700"
            }`}
          >
            Help
          </button>
        </div>

        {activeBoardTab === "main" ? (
          <MessageBoard
            key="arena-board"
            title="Arena Message Board"
            emptyMessage="No threads yet. Be the first to post to the arena board."
          />
        ) : (
          <MessageBoard
            key="help-board"
            apiPrefix="/board/help"
            title="Help Board"
            emptyMessage="No help threads yet. Share tips, answers, or useful guides."
          />
        )}
      </section>

      <Modal open={unrestrictedWarningOpen} onClose={() => setUnrestrictedWarningOpen(false)} title="Before entering 18+ Chat">
        <div className="space-y-4" data-testid="unrestricted-chat-warning-modal">
          <div className="flex items-start gap-3 border border-red-900/40 bg-red-950/10 p-4 rounded-sm">
            <AlertTriangle size={18} className="text-red-300 mt-0.5 shrink-0" />
            <div className="space-y-3 text-sm text-zinc-200 leading-relaxed">
              <p>18+ Chat may contain harsh language and other content some players may find uncomfortable.</p>
              <p>If you are sensitive to this kind of content, you may prefer to stay in the standard Arena Chat instead.</p>
              <p>This chat does not send mentions or message alerts.</p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button type="button" data-testid="unrestricted-chat-warning-cancel" onClick={() => setUnrestrictedWarningOpen(false)} className={btn("ghost")}>
              Go Back
            </button>
            <button type="button" data-testid="unrestricted-chat-warning-confirm" onClick={acceptUnrestrictedWarning} className={btn()}>
              Enter 18+ Chat
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}